/*
 * Sales map for Shopify product pages.
 * Reads city-level points rendered by sections/sales-map.liquid (from the
 * `sales_map_point` metaobjects that Shopify Flow creates when orders ship)
 * and draws them as dots on a zoomable OpenStreetMap street map (Leaflet).
 */
(function () {
  if (customElements.get('sales-map')) return;

  const LEAFLET_JS = 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.js';
  const LEAFLET_CSS = 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.css';
  const TILES = 'https://tile.openstreetmap.org/{z}/{x}/{y}.png';
  const ATTRIBUTION = '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">OpenStreetMap</a> contributors';
  // Continental US bounds: keeps the map framed nicely when there are only a few dots.
  const LOWER_48 = [[24.5, -124.7], [49.4, -67.0]];

  // City-level zoom limit (points are rounded to ~11 km); mouse wheel zooms the map.
  const MAP_SETTINGS = {
    maxZoom: 9,
    scrollWheelZoom: true,
  };

  const loaded = {};
  function loadScript(src) {
    if (!loaded[src]) {
      loaded[src] = new Promise((resolve, reject) => {
        const s = document.createElement('script');
        s.src = src;
        s.onload = resolve;
        s.onerror = () => reject(new Error('Failed to load ' + src));
        document.head.appendChild(s);
      });
    }
    return loaded[src];
  }

  function loadStyle(href) {
    if (!loaded[href]) {
      loaded[href] = new Promise((resolve, reject) => {
        const l = document.createElement('link');
        l.rel = 'stylesheet';
        l.href = href;
        l.onload = resolve;
        l.onerror = () => reject(new Error('Failed to load ' + href));
        document.head.appendChild(l);
      });
    }
    return loaded[href];
  }

  function escapeHtml(text) {
    return String(text).replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
  }

  function parsePoints(scope) {
    const el = scope.querySelector('script[data-sales-map-points]');
    if (!el) return [];
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return [];
    }
  }

  // Liquid can only render 250 metaobject entries per page, so fetch any
  // further pages through the Section Rendering API.
  async function readAllPoints(root) {
    const points = parsePoints(root);
    const pages = parseInt(root.dataset.pages, 10) || 1;
    const sectionId = root.dataset.sectionId;
    if (pages <= 1 || !sectionId) return points;

    const requests = [];
    for (let page = 2; page <= pages; page++) {
      const url = `${location.pathname}?section_id=${encodeURIComponent(sectionId)}&page=${page}`;
      requests.push(
        fetch(url)
          .then((r) => (r.ok ? r.text() : ''))
          .then((html) => parsePoints(new DOMParser().parseFromString(html, 'text/html')))
          .catch(() => [])
      );
    }
    return points.concat(...(await Promise.all(requests)));
  }

  function aggregate(points) {
    const byCity = new Map();
    for (const p of points) {
      const lat = parseFloat(p.lat);
      const lng = parseFloat(p.lng);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) continue;
      // A missing coordinate can arrive as 0 rather than blank; nobody ships to 0,0.
      if (lat === 0 && lng === 0) continue;
      const units = Math.max(1, parseInt(p.units, 10) || 1);
      const city = String(p.city || '').trim();
      const region = String(p.region || '').trim();
      const country = String(p.country || '').trim();
      const key = [city, region, country].join('|').toLowerCase();
      const entry = byCity.get(key) || { city, region, country, lat: 0, lng: 0, n: 0, units: 0 };
      entry.lat += lat;
      entry.lng += lng;
      entry.n += 1;
      entry.units += units;
      byCity.set(key, entry);
    }
    return [...byCity.values()].map((e) => ({ ...e, lat: e.lat / e.n, lng: e.lng / e.n }));
  }

  function placeName(p) {
    const area = p.country === 'US' || p.country === 'CA' ? p.region : p.country;
    return [p.city, area].filter(Boolean).join(', ') || 'Unknown';
  }

  function fillSummary(template, points) {
    const units = points.reduce((sum, p) => sum + p.units, 0);
    const regions = new Set(points.map((p) => `${p.country}|${p.region}`)).size;
    return template
      .replace(/\[units\]/g, units)
      .replace(/\[cities\]/g, points.length)
      .replace(/\[regions\]/g, regions);
  }

  // Resolves a CSS custom property (which may itself use var()/rgb()) to a real color.
  function resolveColor(el, property) {
    const probe = document.createElement('span');
    probe.style.color = `var(${property})`;
    el.appendChild(probe);
    const color = getComputedStyle(probe).color;
    probe.remove();
    return color;
  }

  class SalesMap extends HTMLElement {
    connectedCallback() {
      if (this._started) return;
      this._started = true;
      const io = new IntersectionObserver(
        (entries) => {
          if (entries.some((e) => e.isIntersecting)) {
            io.disconnect();
            this.render().catch((err) => {
              console.warn('[sales-map]', err);
              this.classList.add('sales-map--failed');
            });
          }
        },
        { rootMargin: '300px' }
      );
      io.observe(this);
    }

    disconnectedCallback() {
      if (this._resizeObserver) this._resizeObserver.disconnect();
      if (this._map) this._map.remove();
    }

    // Safe to call more than once (e.g. the theme editor re-renders sections).
    render() {
      if (!this._rendering) this._rendering = this.draw();
      return this._rendering;
    }

    async draw() {
      const points = aggregate(await readAllPoints(this));
      const section = this.closest('.sales-map-section') || this;
      if (!points.length) {
        section.hidden = true;
        return;
      }

      const summary = this.querySelector('[data-sales-map-summary]');
      if (summary && summary.dataset.template) {
        summary.textContent = fillSummary(summary.dataset.template, points);
      }
      const list = this.querySelector('[data-sales-map-list]');
      if (list) {
        list.innerHTML = '';
        for (const p of [...points].sort((a, b) => b.units - a.units)) {
          const li = document.createElement('li');
          li.textContent = `${placeName(p)}: ${p.units}`;
          list.appendChild(li);
        }
      }

      await Promise.all([loadScript(LEAFLET_JS), loadStyle(LEAFLET_CSS)]);
      const L = window.L;

      const canvas = this.querySelector('[data-sales-map-canvas]');
      canvas.innerHTML = '';

      const map = L.map(canvas, {
        maxZoom: MAP_SETTINGS.maxZoom,
        minZoom: 2,
        scrollWheelZoom: MAP_SETTINGS.scrollWheelZoom,
        // On phones one finger should scroll the page; pinch still zooms and pans.
        dragging: !L.Browser.mobile,
        worldCopyJump: true,
      });
      this._map = map;
      L.tileLayer(TILES, { maxZoom: 19, attribution: ATTRIBUTION }).addTo(map);

      const dotColor = resolveColor(this, '--sales-map-dot');
      const unitLabel = this.dataset.unitLabel || 'sold';
      const maxUnits = Math.max(4, ...points.map((p) => p.units));
      const radius = (units) => Math.max(6, 18 * Math.sqrt(units / maxUnits));

      const bounds = L.latLngBounds([]);
      for (const p of [...points].sort((a, b) => b.units - a.units)) {
        const r = radius(p.units);
        L.circleMarker([p.lat, p.lng], {
          radius: r,
          color: '#ffffff',
          weight: 2,
          fillColor: dotColor,
          fillOpacity: 0.8,
        })
          .bindTooltip(`<strong>${escapeHtml(placeName(p))}</strong><br>${p.units} ${escapeHtml(unitLabel)}`, {
            direction: 'top',
            offset: [0, -r],
          })
          .addTo(map);
        bounds.extend([p.lat, p.lng]);
      }

      const inNorthAmerica = points.every((p) => p.lng > -170 && p.lng < -50 && p.lat > 10 && p.lat < 75);
      if (inNorthAmerica) bounds.extend(LOWER_48);
      map.fitBounds(bounds, { padding: [24, 24], maxZoom: Math.min(6, MAP_SETTINGS.maxZoom) });

      // Leaflet measures its container once; keep it in sync when the layout changes.
      this._resizeObserver = new ResizeObserver(() => map.invalidateSize());
      this._resizeObserver.observe(canvas);

      this.classList.add('sales-map--ready');
    }
  }

  customElements.define('sales-map', SalesMap);
})();
